// ===========================================
// DatabaseStatus.js - Database Connection Status Component
// ===========================================
import React, { useEffect, useState } from 'react';
import { Database, CheckCircle, XCircle, Clock } from 'lucide-react';
import { api } from '../utils/api';

const DatabaseStatus = ({ className = "" }) => {
  const [status, setStatus] = useState({
    isHealthy: null,
    isLoading: true,
    lastChecked: null,
    error: null
  });

  const checkHealth = async () => {
    try {
      setStatus(prev => ({ ...prev, isLoading: true }));
      const response = await api.healthCheck();
      
      setStatus({
        isHealthy: response.status === 'healthy',
        isLoading: false,
        lastChecked: new Date().toLocaleTimeString(),
        error: null
      });
    } catch (error) {
      setStatus({
        isHealthy: false,
        isLoading: false,
        lastChecked: new Date().toLocaleTimeString(),
        error: error.message
      });
    }
  };

  useEffect(() => {
    checkHealth();
    
    // Check health every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = () => {
    if (status.isLoading) {
      return <Clock className="w-4 h-4 text-blue-500 animate-pulse" />;
    }
    
    if (status.isHealthy === true) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusText = () => {
    if (status.isLoading) return "Checking...";
    if (status.isHealthy === true) return "Connected";
    return "Disconnected";
  };

  const getStatusColor = () => {
    if (status.isLoading) return "text-blue-600";
    if (status.isHealthy === true) return "text-green-600";
    return "text-red-600";
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Database className="w-4 h-4 text-gray-500" />
      <div className="flex items-center gap-1">
        {getStatusIcon()}
        <span className={`text-sm font-medium ${getStatusColor()}`}>
          {getStatusText()}
        </span>
      </div>
      
      {status.lastChecked && (
        <span className="text-xs text-gray-500">
          {status.lastChecked}
        </span>
      )}
      
      {status.error && (
        <span className="text-xs text-red-500" title={status.error}>
          Error
        </span>
      )}
    </div>
  );
};

export default DatabaseStatus;
